import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { profileApi, type CouponItem } from "@/api";
import { auth } from "@/lib/auth";
import { buildLoginRedirect } from "@/lib/navigation";
import { RequestError } from "@/lib/request";

interface Coupon {
  id: number;
  title: string;
  desc: string;
  amount: string;
  unit: "元";
  condition: string;
  expiry: string;
  category: string;
  status: "available" | "used" | "expired";
  urgent: boolean;
}

type TabKey = "available" | "used" | "expired";

const STATUS_TYPE_MAP: Record<TabKey, number> = {
  available: 0,
  used: 1,
  expired: 2,
};

function normalizeDateText(value?: string) {
  if (!value) {
    return "长期有效";
  }
  return value.replace("T", " ").slice(0, 10);
}

function calculateDaysLeft(value?: string) {
  if (!value) {
    return null;
  }
  const target = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(target.getTime())) {
    return null;
  }
  return Math.ceil((target.getTime() - Date.now()) / 86400000);
}

function mapCouponStatus(status: number): TabKey {
  if (status === 1) {
    return "used";
  }
  if (status === 2) {
    return "expired";
  }
  return "available";
}

function mapCouponItem(item: CouponItem): Coupon {
  const status = mapCouponStatus(Number(item.status || 0));
  const expiry = normalizeDateText(item.use_end_time_text);
  const daysLeft = calculateDaysLeft(item.use_end_time_text);

  return {
    id: item.id,
    title: String(item.tips || "平台优惠券"),
    desc: String(item.tips || "下单结算时可使用"),
    amount: String(item.money || "0"),
    unit: "元",
    condition: String(item.condition || "无门槛"),
    expiry,
    category: "通用券",
    status,
    urgent: status === "available" && daysLeft !== null && daysLeft <= 7,
  };
}

function CouponCard({ coupon, onUse }: { coupon: Coupon; onUse: () => void }) {
  const isActive = coupon.status === "available";
  const isUsed = coupon.status === "used";

  const daysLeft = () => {
    const diff = Math.ceil((new Date(coupon.expiry).getTime() - Date.now()) / 86400000);
    return diff;
  };
  const days = isActive ? daysLeft() : null;

  return (
    <div
      className="relative flex min-w-0 overflow-hidden rounded-2xl"
      style={{
        border: `1.5px solid ${isActive ? (coupon.urgent ? "#FF9A3C" : "#F0EAE0") : "#EEEAE4"}`,
        background: isActive ? "#fff" : "#FAFAF8",
        opacity: isActive ? 1 : 0.72,
      }}
    >
      <div
        className="relative flex flex-shrink-0 flex-col items-center justify-center px-5 py-5"
        style={{
          background: isActive
            ? "linear-gradient(160deg, #FF6018 0%, #FF9A3C 100%)"
            : "#E8E0D8",
          minWidth: 88,
        }}
      >
        <div
          className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full"
          style={{ background: isActive ? "#fff" : "#FAFAF8", border: `1.5px solid ${isActive ? (coupon.urgent ? "#FF9A3C" : "#F0EAE0") : "#EEEAE4"}` }}
        />
        <span
          className="text-2xl font-black leading-none"
          style={{ color: isActive ? "#fff" : "#C0B8AE" }}
        >
          {coupon.amount}
        </span>
        <span
          className="text-xs font-semibold mt-0.5"
          style={{ color: isActive ? "rgba(255,255,255,0.85)" : "#C0B8AE" }}
        >
          {coupon.unit}
        </span>
        <span
          className="mt-1.5 max-w-full rounded-full px-2 py-0.5 text-center text-[9px] leading-tight whitespace-normal break-words"
          style={{
            background: isActive ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.06)",
            color: isActive ? "rgba(255,255,255,0.8)" : "#B0A898",
          }}
        >
          {coupon.condition}
        </span>
      </div>

      <div className="flex-1 min-w-0 px-4 py-4 flex flex-col justify-between">
        <div>
          <div className="mb-1 flex min-w-0 items-start justify-between gap-2">
            <p
              className="min-w-0 flex-1 break-words text-sm font-bold leading-tight"
              style={{ color: isActive ? "#1A1A1A" : "#999" }}
            >
              {coupon.title}
            </p>
            {coupon.urgent && isActive ? (
              <span
                className="text-[9px] px-1.5 py-0.5 rounded-full font-bold whitespace-nowrap flex-shrink-0"
                style={{ background: "#FFF0E6", color: "#FF5C00" }}
              >
                即将到期
              </span>
            ) : null}
            {isUsed ? (
              <span className="text-[10px] font-semibold whitespace-nowrap flex-shrink-0" style={{ color: "#C0B8AE" }}>已使用</span>
            ) : null}
            {coupon.status === "expired" ? (
              <span className="text-[10px] font-semibold whitespace-nowrap flex-shrink-0" style={{ color: "#C0B8AE" }}>已过期</span>
            ) : null}
          </div>
          <p className="text-[11px] leading-relaxed" style={{ color: "#BBAAA0" }}>{coupon.desc}</p>
        </div>
        <div className="mt-2.5 flex min-w-0 items-center justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <span
              className="text-[10px] px-2 py-0.5 rounded-full"
              style={{ background: isActive ? "#FFF4EC" : "#F5F2EE", color: isActive ? "#FF9A3C" : "#C0B8AE" }}
            >
              {coupon.category}
            </span>
            <span className="min-w-0 truncate text-[10px]" style={{ color: "#D0C8BE" }}>
              {isActive && days !== null
                ? days <= 7
                  ? `剩余 ${days} 天`
                  : `至 ${coupon.expiry}`
                : `至 ${coupon.expiry}`}
            </span>
          </div>
          {isActive ? (
            <button
              onClick={onUse}
              className="flex-shrink-0 whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-bold text-white"
              style={{ background: "linear-gradient(90deg, #FF5C00, #FF9A3C)" }}
            >
              去使用
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default function CouponsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<TabKey>("available");
  const [inputCode, setInputCode] = useState("");
  const [codeMsg, setCodeMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [couponMap, setCouponMap] = useState<Record<TabKey, Coupon[]>>({
    available: [],
    used: [],
    expired: [],
  });
  const [tabCounts, setTabCounts] = useState<Record<TabKey, number>>({
    available: 0,
    used: 0,
    expired: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadCoupons = async () => {
      if (!auth.getToken()) {
        navigate(buildLoginRedirect(`${location.pathname}${location.search}`), { replace: true });
        return;
      }

      setLoading(true);
      setError("");

      try {
        const [availableResult, usedResult, expiredResult] = await Promise.all([
          profileApi.getCouponList({ type: STATUS_TYPE_MAP.available, page_no: 1, page_size: 100 }),
          profileApi.getCouponList({ type: STATUS_TYPE_MAP.used, page_no: 1, page_size: 100 }),
          profileApi.getCouponList({ type: STATUS_TYPE_MAP.expired, page_no: 1, page_size: 100 }),
        ]);

        if (cancelled) {
          return;
        }

        setCouponMap({
          available: (availableResult.lists || []).map(mapCouponItem),
          used: (usedResult.lists || []).map(mapCouponItem),
          expired: (expiredResult.lists || []).map(mapCouponItem),
        });
        setTabCounts({
          available: Number(availableResult.count || 0),
          used: Number(usedResult.count || 0),
          expired: Number(expiredResult.count || 0),
        });
      } catch (requestError) {
        if (cancelled) {
          return;
        }
        if (requestError instanceof RequestError && requestError.code === 1001) {
          navigate(buildLoginRedirect(`${location.pathname}${location.search}`), { replace: true });
          return;
        }
        setError(requestError instanceof Error ? requestError.message : "优惠券加载失败，请稍后重试");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadCoupons();

    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search, navigate]);

  const tabs = useMemo(
    () => [
      { key: "available", label: "可使用", count: tabCounts.available },
      { key: "used", label: "已使用", count: tabCounts.used },
      { key: "expired", label: "已过期", count: tabCounts.expired },
    ] as const,
    [tabCounts.expired, tabCounts.available, tabCounts.used],
  );

  const filtered = couponMap[activeTab];

  const handleRedeemCode = () => {
    if (!inputCode.trim()) {
      return;
    }
    setCodeMsg({ type: "error", text: "当前版本暂未接入兑换码功能" });
    setTimeout(() => setCodeMsg(null), 3000);
    setInputCode("");
  };

  return (
    <div
      className="customer-page-shell flex flex-col min-h-screen pb-8"
      style={{ background: "#F5F4F1", paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 32px)" }}
    >
      <div
        className="relative overflow-hidden px-5 pt-12 pb-5"
        style={{ background: "linear-gradient(150deg, #FFB07A 0%, #FF8040 55%, #FF6018 100%)" }}
      >
        <div className="absolute -top-8 -right-8 w-36 h-36 rounded-full" style={{ background: "rgba(255,255,255,0.06)" }} />
        <div className="relative z-10 mb-5 flex min-w-0 items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-full flex items-center justify-center cursor-pointer flex-shrink-0"
            style={{ background: "rgba(255,255,255,0.2)" }}
          >
            <i className="ri-arrow-left-s-line text-lg text-white" />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="text-base font-bold text-white">我的优惠券</h1>
            <p className="text-[11px] mt-0.5" style={{ color: "rgba(255,255,255,0.7)" }}>
              共 {tabCounts.available} 张可用
            </p>
          </div>
        </div>

        <div className="relative z-10">
          <div
            className="flex min-w-0 items-center gap-2 rounded-2xl px-4 py-2.5"
            style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.25)" }}
          >
            <i className="ri-gift-line text-sm text-white opacity-70 flex-shrink-0" />
            <input
              type="text"
              value={inputCode}
              onChange={(event) => setInputCode(event.target.value.toUpperCase())}
              onKeyDown={(event) => event.key === "Enter" && handleRedeemCode()}
              placeholder="输入兑换码领取优惠券"
              className="min-w-0 flex-1 bg-transparent text-sm text-white outline-none placeholder:opacity-50"
            />
            <button
              onClick={handleRedeemCode}
              disabled={!inputCode.trim()}
              className="flex-shrink-0 whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-bold transition-all"
              style={{
                background: inputCode.trim() ? "#fff" : "rgba(255,255,255,0.2)",
                color: inputCode.trim() ? "#FF6018" : "rgba(255,255,255,0.5)",
              }}
            >
              兑换
            </button>
          </div>
          {codeMsg ? (
            <div
              className="mt-2 px-4 py-2 rounded-xl text-xs font-medium text-center"
              style={{
                background: codeMsg.type === "success" ? "rgba(23,168,123,0.25)" : "rgba(220,80,60,0.25)",
                color: "#fff",
              }}
            >
              {codeMsg.text}
            </div>
          ) : null}
        </div>
      </div>

      <div
        className="sticky top-0 z-10 overflow-x-auto px-5 pb-1 pt-4"
        style={{ background: "#F5F4F1" }}
      >
        <div className="flex min-w-full gap-0">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className="flex min-w-[88px] flex-1 shrink-0 items-center justify-center gap-1.5 pb-3 text-sm font-semibold cursor-pointer whitespace-nowrap transition-all"
              style={{
                color: activeTab === tab.key ? "#FF6018" : "#BBAAA0",
                borderBottom: `2px solid ${activeTab === tab.key ? "#FF6018" : "transparent"}`,
              }}
            >
              {tab.label}
              {tab.count > 0 ? (
                <span
                  className="min-w-[18px] rounded-full px-1.5 py-0.5 text-center text-[10px] font-bold"
                  style={{
                    background: activeTab === tab.key ? "#FF6018" : "#E8E0D8",
                    color: activeTab === tab.key ? "#fff" : "#B0A898",
                  }}
                >
                  {tab.count}
                </span>
              ) : null}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 pt-2 pb-4 space-y-3 flex-1">
        {error ? (
          <div
            className="rounded-2xl px-4 py-3 text-sm"
            style={{ background: "#FFF4EC", color: "#D94A00", border: "1px solid #FFD8BF" }}
          >
            {error}
          </div>
        ) : null}

        {loading ? (
          <div className="flex flex-col items-center py-16">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
              style={{ background: "#FFF4EC" }}
            >
              <i className="ri-loader-4-line text-3xl animate-spin" style={{ color: "#FFB07A" }} />
            </div>
            <p className="text-sm font-semibold mb-1" style={{ color: "#BBAAA0" }}>优惠券加载中...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center py-16">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
              style={{ background: "#FFF4EC" }}
            >
              <i className="ri-coupon-3-line text-3xl" style={{ color: "#FFB07A" }} />
            </div>
            <p className="text-sm font-semibold mb-1" style={{ color: "#BBAAA0" }}>
              {activeTab === "available" ? "暂无可用优惠券" : activeTab === "used" ? "暂无已使用记录" : "暂无过期券"}
            </p>
            {activeTab === "available" ? (
              <p className="text-xs" style={{ color: "#CCBBB0" }}>完成订单可获得优惠券奖励</p>
            ) : null}
          </div>
        ) : (
          filtered.map((coupon) => (
            <CouponCard
              key={coupon.id}
              coupon={coupon}
              onUse={() => navigate("/")}
            />
          ))
        )}
      </div>

      {activeTab === "available" && filtered.length > 0 ? (
        <div className="px-4 pb-4">
          <div
            className="flex items-start gap-2.5 p-3.5 rounded-xl"
            style={{ background: "#F8F6F3" }}
          >
            <i className="ri-information-line text-sm flex-shrink-0 mt-0.5" style={{ color: "#C0B0A0" }} />
            <p className="text-[11px] leading-relaxed" style={{ color: "#C0B0A0" }}>
              优惠券在预约结算时可选择使用，每次仅限一张，部分项目有限制条件，请以实际结算页面为准。
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
